import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../store/useUserStore';

// Query keys
const ORDER_KEYS = {
  all: ['orders'] as const,
  lists: () => [...ORDER_KEYS.all, 'list'] as const,
  list: (customerId: number) => [...ORDER_KEYS.lists(), customerId] as const,
  details: () => [...ORDER_KEYS.all, 'detail'] as const,
  detail: (orderId: number) => [...ORDER_KEYS.details(), orderId] as const,
};

/** Misma ruta base que useGetMyOrdersWithDetails / useCreateOrderFromCart (deuda: i18n). */
const ORDERS_API_PREFIX = '/api/es/orders';

export const useOrders = () => {
  const queryClient = useQueryClient();
  const { profile } = useUserStore();

  // Obtener órdenes de un cliente (vía API; no importar OrderService en cliente — evita mysql2 en el bundle)
  const useGetOrdersByCustomerId = (customerId: number) => {
    return useQuery({
      queryKey: ORDER_KEYS.list(customerId),
      queryFn: async () => {
        const response = await fetch(
          `${ORDERS_API_PREFIX}?customerId=${customerId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.error || 'Error obteniendo órdenes del cliente'
          );
        }

        if (!result.success) {
          throw new Error(result.error || 'Error en la respuesta del servidor');
        }

        return result.data || [];
      },
      enabled: !!customerId,
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos
    });
  };

  // Obtener órdenes del usuario autenticado
  const useGetMyOrders = () => {
    return useGetOrdersByCustomerId(profile?.id || 0);
  };

  // Hook para obtener órdenes con detalles completos
  const useGetMyOrdersWithDetails = () => {
    const customerId = profile?.id || 0;
    return useQuery({
      queryKey: [...ORDER_KEYS.list(customerId), 'with-details'],
      queryFn: async () => {
        const response = await fetch(
          `/api/es/orders/customer/${customerId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || 'Error obteniendo órdenes con detalles'
          );
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Error en la respuesta del servidor');
        }

        return result.data || [];
      },
      enabled: !!customerId,
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos
    });
  };

  // Crear orden desde carrito
  const useCreateOrderFromCart = () => {
    return useMutation({
      mutationFn: async ({
        cartId,
        couponCode,
      }: {
        cartId: number;
        couponCode: string;
      }) => {
        const response = await fetch(`/api/es/orders/cart/${cartId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ couponCode }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error creando orden desde carrito');
        }

        const result = await response.json();
        return result;
      },
      onSuccess: (data, variables) => {
        // Invalidar la lista de órdenes del cliente
        queryClient.invalidateQueries({
          queryKey: ORDER_KEYS.list(variables.cartId), // Usar cartId como referencia
        });

        // Invalidar también las órdenes del usuario autenticado
        if (profile?.id) {
          queryClient.invalidateQueries({
            queryKey: ORDER_KEYS.list(profile.id),
          });
        }

        console.log('✅ Orden creada desde carrito exitosamente:', data?.id);
      },
      onError: (error) => {
        console.error('❌ Error creando orden desde carrito:', error);
      },
    });
  };

  // Crear orden desde carrito vía módulo Dok7 plugin
  const useCreateOrderFromCartByPlugin = () => {
    return useMutation({
      mutationFn: async ({ cartId }: { cartId: number }) => {
        const response = await fetch(`${ORDERS_API_PREFIX}/cart/plugin/${cartId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Error creando orden desde carrito (plugin)');
        }

        if (!result.success) {
          throw new Error(result.error || 'Error en la respuesta del servidor');
        }

        return result;
      },
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ORDER_KEYS.list(variables.cartId),
        });

        if (profile?.id) {
          queryClient.invalidateQueries({
            queryKey: ORDER_KEYS.list(profile.id),
          });
        }

        console.log('✅ Orden creada vía Dok7 plugin:', data?.data?.id);
      },
      onError: (error) => {
        console.error('❌ Error creando orden vía Dok7 plugin:', error);
      },
    });
  };

  return {
    // Queries
    useGetMyOrders,
    useGetMyOrdersWithDetails,

    // Mutations
    useCreateOrderFromCart,
    useCreateOrderFromCartByPlugin,
  };
};
